import React, { useState } from "react";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Skeleton } from "antd";
import { CustomDataTable } from "@/components/UI/Tables/Datatable";
import { useCategoryData } from "../hooks/useCategoryData";
import "./CategoryTable.css";

/**
 * Image component with loading state
 */
const CategoryImage = ({ src, alt, categoryId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  return (
    <div className="relative w-10 h-10">
      {loading && !error && (
        <Skeleton.Avatar
          active
          size={40}
          shape="circle"
          className="absolute inset-0"
        />
      )}
      {src && !error ? (
        <img
          src={src}
          alt={alt}
          className={`w-10 h-10 rounded-full object-cover border border-gray-200 ${
            loading ? 'opacity-0' : 'opacity-100'
          } transition-opacity duration-200`}
          onLoad={() => setLoading(false)}
          onError={() => {
            setLoading(false);
            setError(true);
          }}
        />
      ) : null}
      {(error || !src) && !loading && (
        <div className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center">
          <span className="text-gray-400 text-xs">📦</span>
        </div>
      )}
    </div>
  );
};

/**
 * Company Categories Table Component
 * Uses the reusable Datatable component with server-side pagination and search
 */
const CategoryTable = ({ onAddCategory, onEditCategory, onViewCategory, onDeleteCategory }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Use custom hook for category data with pagination and search
  const {
    categoriesData,
    paginationInfo,
    handlePageChange,
    handlePageSizeChange,
    search,
    handleSearch,
    isLoading,
    isFetching,
    refetch,
    error,
  } = useCategoryData();

  // Define columns for categories data
  const categoryColumns = [
    {
      title: "Category Name",
      dataIndex: "name",
      key: "name",
      width: 300,
      render: (text, record) => (
        <div className="flex items-center gap-3">
          <CategoryImage
            src={record.image}
            alt={text}
            categoryId={record.id}
          />
          <div className="flex flex-col">
            <span className="font-medium text-gray-900">{text}</span>
            <span className="text-sm text-gray-500">SFS{record.id}</span>
          </div>
        </div>
      ),
    },
    {
      title: "Products",
      dataIndex: "product_count",
      key: "product_count",
      width: 120,
      render: (product_count) => (
        <span className="font-medium text-gray-900">{product_count || 0}</span>
      ),
    },
  ];

  // Action buttons for the table
  const actionButtons = [
    {
      label: "Add Category",
      type: "primary",
      icon: <PlusOutlined />,
      onClick: onAddCategory,
      className: "bg-orange-500 border-orange-500 hover:bg-orange-600",
    },
  ];

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  // Row actions for category table
  const rowActions = [
    {
      label: "View Details",
      icon: <EyeOutlined />,
      onClick: (record) => onViewCategory && onViewCategory(record),
    },
    {
      label: "Edit Category",
      icon: <EditOutlined />,
      onClick: (record) => onEditCategory && onEditCategory(record),
    },
    {
      label: "Delete Category",
      icon: <DeleteOutlined />,
      onClick: (record) => onDeleteCategory && onDeleteCategory(record),
    },
  ];

  // Handle bulk actions
  const handleBulkDelete = (selectedKeys) => {
    // console.log("Bulk delete categories:", selectedKeys);
    // Implement bulk delete logic here
  };

  const handleBulkEdit = (selectedKeys) => {
    // console.log("Bulk edit categories:", selectedKeys);
    // Implement bulk edit logic here
  };

  // Handle loading and error states
  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex flex-col items-center">
          <div className="text-md text-red-600 text-center">
            Error loading categories: <br />
            {error?.message || "Please check your internet connection"}
          </div>
          <button
            onClick={() => refetch()}
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <CustomDataTable
      title="Categories"
      data={categoriesData}
      columns={categoryColumns}
      showSearch={true}
      searchPlaceholder="Search categories..."
      searchValue={search}
      onServerSearch={handleSearch}
      searchMode="server"
      actionButtons={actionButtons}
      showFilter={false}
      showCSV={true}
      paginationMode="server"
      currentPage={paginationInfo.current_page}
      totalItems={paginationInfo.total}
      serverPageSize={paginationInfo.per_page}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      rowSelection={rowSelection}
      selectedRowKeys={selectedRowKeys}
      rowActions={rowActions}
      loading={isLoading || isFetching}
      tableClassName="category-table"
      footerInfo={{
        left: `${selectedRowKeys.length} of ${categoriesData.length} row(s) selected.`,
      }}
    />
  );
};

export default CategoryTable;